import React, { useCallback, useEffect } from 'react';
import './RequestDetails.css';
import Layout from '../../../../../components/Layout';
import AdminNavbar from '../../../../../components/Admin/AdminNavbar/AdminNavbar';
import { Avatar, Box, Divider, Input, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import ApproveModal from '../../../../../components/Admin/Request/ApproveModal/ApproveModal';
import RejectModal from '../../../../../components/Admin/Request/RejectModal/RejectModal';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import { formatDate, formatDateShort, formatDateWithTime } from '../../../../../utils/dateUtils';
import { useAuth } from '../../../../../context/AuthContext';
import DocumentLogo from '../../../../../assets/Admin/document-logo.svg';
import { toast } from 'react-hot-toast';
import { showSuccessToast } from '../../../../../utils/toastUtils';

const StatusColor = {
  approval_pending: { color: '#EBBF37', bgcolor: '#FFFAEC', value: 'Pending' },
  approved: { color: '#14B334', bgcolor: '#F2FFF5', value: 'Approved' },
  rejected: { color: '#E14949', bgcolor: '#FFE5E5', value: 'Rejected' },
  requested_for_reverification: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'Re-verification' },
};

const LevelToTeachColor = {
  elementary: {
    color: '#14B334',
    bgcolor: '#F2FFF5',
    value2: 'Elementary',
  },
  university: {
    color: '#14B334',
    bgcolor: '#F2FFF5',
    value2: 'University',
  },
  high_school: { color: '#E0AD14', bgcolor: '#FFFBF0', value2: 'High School' },
  middle_school: {
    color: '#6B60E7',
    bgcolor: '#F0EFFF',
    value2: 'Middle School',
  },
  beginner: {
    color: '#14B334',
    bgcolor: '#F2FFF5',
    value: 'BG',
    value2: 'Beginner',
  },
  intermediate: {
    color: '#E0AD14',
    bgcolor: '#FFFBF0',
    value: 'IN',
    value2: 'Intermediate',
  },

  professional: {
    color: '#E14949',
    bgcolor: '#FFF2F2',
    value: 'PR',
    value2: 'Professional',
  },
};

const RequestDetails = () => {
  const [auth] = useAuth();
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [teacherDetails, setTeacherDetails] = useState([]);
  const [commentList, setCommentList] = useState([]);
  const [bankDetails, setBankDetails] = useState([]);
  const [rbacMe, setRbacMe] = useState(null);
  const { id } = useParams();
  const [rowId, setRowId] = useState(null);
  const [teacherName, setTeacherName] = useState('');
  const [comment, setComment] = useState('');
  const navigate = useNavigate();
  const [addComment, setAddComment] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setRowId(id);
  }, [id]);

  useEffect(() => {
    const TeacherName = `${teacherDetails?.first_name} ${teacherDetails?.last_name}`;
    setTeacherName(TeacherName);
  }, [teacherDetails]);

  const getSingleTeachersDetails = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/admin/teachers/${id}`);
      setTeacherDetails(data);
    } catch (error) {
      console.log(error);
    }
  }, [id]);

  const getSingleTeachersBankDetails = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/admin/bank_details/${id}`);
      setBankDetails(data);
    } catch (error) {
      console.log(error);
    }
  }, [id]);
  const getTeacherAllCommentDetails = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/admin/teachers/${id}/comments`);
      // API may return an array or a paginated object with results
      setCommentList(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      console.log(error);
    }
  }, [id]);

  useEffect(() => {
    getSingleTeachersDetails();
    getSingleTeachersBankDetails();
    getTeacherAllCommentDetails();
  }, [
    getSingleTeachersBankDetails,
    getSingleTeachersDetails,
    getTeacherAllCommentDetails,
    addComment,
    id,
  ]);

  const handleApprove = () => {
    setOpenApproveModal(true);
  };
  const handleCommentChange = e => {
    setComment(e.target.value);
  };
  const handleReject = () => {
    setOpenRejectModal(true);
  };

  const handleSubmit = async () => {
    const trimmedComment = comment.trim();
    if (!trimmedComment || isSubmitting) return;
    try {
      setIsSubmitting(true);
      const res = await axiosInstance.post(`/admin/comments`, {
        teacher_id: id,
        comment: trimmedComment,
      });
      if (res?.status === 201 || res?.status === 200) {
        setComment('');
        setAddComment(prev => !prev);
        showSuccessToast('Comment added successfully');
      }
    } catch (error) {
      console.log(error);
      const errorMessage = error.response?.data?.detail || 'Failed to add comment';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  function formatText(input) {
    return input
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
  }

  const getInitials = (firstName, lastName) => {
    const firstInitial = firstName?.charAt(0)?.toUpperCase() || '';
    const lastInitial = lastName?.charAt(0)?.toUpperCase() || '';
    const initials = firstInitial + lastInitial;
    return initials || '';
  };

  const formatRole = roles => {
    if (!roles || !Array.isArray(roles) || roles.length === 0) return '';
    return roles.map(role => formatText(role)).join(', ');
  };

  const getCommentRole = item => {
    if (item?.admin_id === auth?.id || String(item?.admin_id) === String(auth?.id)) {
      const roles = Array.isArray(rbacMe?.roles) ? rbacMe.roles : [];
      return formatRole(roles);
    }
    return '';
  };

  useEffect(() => {
    const fetchCurrentUserRoles = async () => {
      try {
        let cachedData = null;
        try {
          const cached = sessionStorage.getItem('rbac_admin_me');
          if (cached) {
            cachedData = JSON.parse(cached);
            setRbacMe(cachedData);
          }
        } catch (_) {}

        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        setRbacMe(data);
        try {
          sessionStorage.setItem('rbac_admin_me', JSON.stringify(data));
        } catch (_) {}
      } catch (error) {
        console.error('Error fetching RBAC admin profile:', error);
        if (!rbacMe) setRbacMe({ roles: [], permissions: [] });
      }
    };
    fetchCurrentUserRoles();
  }, []);

  return (
    <Layout title="Klass Online | Request details">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-3">
            <Box className="flex justify-between items-center">
              <Box className="flex flex-col">
                <Box className="flex items-center gap-2">
                  <Typography
                    className="user-management-request-text"
                    onClick={() => navigate('/admin/request')}
                  >
                    Request {'>'}
                    {'>'}
                  </Typography>
                  <Typography className="profile-text">Profile</Typography>
                </Box>
                <Box className="flex items-center gap-4">
                  <Typography className="teacher-request-name-text">
                    {teacherDetails?.first_name} {teacherDetails?.last_name}
                  </Typography>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      height: '16px',
                      mt: 1,
                      borderColor: '#D5DEE3',
                    }}
                  />
                  <Box
                    sx={{
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '4px 8px 4px 8px',
                      borderRadius: '20px',
                      backgroundColor: StatusColor[teacherDetails?.approval_status]?.bgcolor,
                      color: StatusColor[teacherDetails?.approval_status]?.color,
                      border: `1px solid ${StatusColor[teacherDetails?.approval_status]?.color}`,
                      fontFamily: 'Poppins !important',
                      fontSize: '10px !important',
                      fontStyle: 'normal !important',
                      fontWeight: '400 !important',
                      lineHeight: 'normal !important',
                    }}
                  >
                    <Typography
                      className="w-[6px] h-[6px] rounded-full"
                      style={{
                        backgroundColor: StatusColor[teacherDetails?.approval_status]?.color,
                      }}
                    ></Typography>
                    <Typography className="status-text">
                      {StatusColor[teacherDetails?.approval_status]?.value}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {teacherDetails?.approval_status === 'approval_pending' && (
                <Box className="flex items-center gap-4">
                  <button className="approve-btn" onClick={handleApprove}>
                    Approve
                  </button>
                  <button className="reject-btn" onClick={handleReject}>
                    Reject
                  </button>
                </Box>
              )}
            </Box>
            <Box className="flex flex-col items-start p-[16px] gap-[19px] bg-[#FFF] rounded-[8px]">
              <Box className="flex flex-col gap-1 w-[100%]">
                {/* Conditional Rendering for Review or Koins Deducted */}
                {teacherDetails?.approval_status === 'rejected' ? (
                  <Box className="status-container rejected">
                    <Box className="status-info">
                      <Typography className="rejected-status-label">Rejected by</Typography>
                      <Typography className="rejected-status-name">
                        {formatText(
                          teacherDetails.update_approval_by
                            ? teacherDetails.update_approval_by
                            : '---'
                        )}
                      </Typography>
                    </Box>
                    <Box className="status-info">
                      <Typography className="rejected-status-label">Date</Typography>
                      <Typography className="rejected-status-name">
                        {teacherDetails?.rejected_at
                          ? formatDateShort(teacherDetails?.rejected_at)
                          : '---'}
                      </Typography>
                    </Box>
                    <Box className="status-info">
                      <Typography className="rejected-status-label">Reason</Typography>
                      <Typography className="rejected-status-name">
                        {formatText(
                          teacherDetails.rejection_reason
                            ? teacherDetails.rejection_reason
                            : 'No reason mentioned'
                        )}
                      </Typography>
                    </Box>
                    <Box className="status-info">
                      <Typography className="rejected-status-label">Coupon Used</Typography>
                      <Typography className="rejected-status-name">
                        {teacherDetails?.used_coupon_code
                          ? teacherDetails?.used_coupon_code
                          : '---'}
                      </Typography>
                    </Box>
                  </Box>
                ) : teacherDetails?.approval_status === 'requested_for_reverification' ? (
                  <Box className="status-container requested-reverification">
                    <Box className="status-info">
                      <Typography className="rejected-status-label">
                        Re-verification requested by
                      </Typography>
                      <Typography className="rejected-status-name">
                        {formatText(
                          teacherDetails.update_approval_by
                            ? teacherDetails.update_approval_by
                            : '---'
                        )}
                      </Typography>
                    </Box>
                    <Box className="status-info">
                      <Typography className="rejected-status-label">Date</Typography>
                      <Typography className="rejected-status-name">
                        {teacherDetails?.rejected_at
                          ? formatDateShort(teacherDetails?.rejected_at)
                          : '---'}
                      </Typography>
                    </Box>
                    <Box className="status-info">
                      <Typography className="rejected-status-label">Reason</Typography>
                      <Typography className="rejected-status-name">
                        {formatText(
                          teacherDetails.rejection_reason
                            ? teacherDetails.rejection_reason
                            : 'No reason mentioned'
                        )}
                      </Typography>
                    </Box>
                    <Box className="status-info">
                      <Typography className="rejected-status-label">Coupon Used</Typography>
                      <Typography className="rejected-status-name">
                        {teacherDetails?.used_coupon_code
                          ? teacherDetails?.used_coupon_code
                          : '---'}
                      </Typography>
                    </Box>
                  </Box>
                ) : teacherDetails?.approval_status === 'approved' ? (
                  <Box className="status-container approved">
                    <Box className="status-info">
                      <Typography className="approved-status-label">Approved by</Typography>
                      <Typography className="approved-status-name">
                        {formatText(
                          teacherDetails.update_approval_by
                            ? teacherDetails.update_approval_by
                            : '---'
                        )}
                      </Typography>
                    </Box>
                    <Box className="status-info">
                      <Typography className="approved-status-label">Date</Typography>
                      <Typography className="approved-status-name">
                        {teacherDetails?.approved_at
                          ? formatDateShort(teacherDetails?.approved_at)
                          : '---'}
                      </Typography>
                    </Box>
                    <Box className="status-info">
                      <Typography className="approved-status-label">Coupon Used</Typography>
                      <Typography className="approved-status-name">
                        {teacherDetails?.used_coupon_code
                          ? teacherDetails?.used_coupon_code
                          : '---'}
                      </Typography>
                    </Box>
                  </Box>
                ) : teacherDetails?.approval_status === 'approval_pending' &&
                  teacherDetails?.used_coupon_code ? (
                  <Box className="status-container pending-coupon">
                    <Box className="status-info">
                      <Typography className="pending-coupon-label">Coupon Used</Typography>
                      <Typography className="pending-coupon-name">
                        {teacherDetails?.used_coupon_code}
                      </Typography>
                    </Box>
                  </Box>
                ) : null}
                <Typography className="personal-information-text">Personal Information</Typography>
                <Box className="flex flex-wrap items-center content-center gap-6 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">First Name</Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {formatText(teacherDetails?.first_name ? teacherDetails?.first_name : '---')}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">Last Name</Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {formatText(teacherDetails?.last_name ? teacherDetails?.last_name : '---')}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">Email</Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {teacherDetails?.email ? teacherDetails?.email : '---'}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">Phone Number</Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {formatText(teacherDetails?.phone_no ? teacherDetails?.phone_no : '---')}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">
                      Current Location
                    </Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {(() => {
                        const city = teacherDetails?.city;
                        const location = teacherDetails?.current_location;
                        if (city && location && city !== location) {
                          return formatText(`${city}, ${location}`);
                        }
                        if (city) {
                          return formatText(city);
                        }
                        if (location) {
                          return formatText(location);
                        }
                        return '---';
                      })()}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">Date of Birth</Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {teacherDetails?.dob ? formatDate(teacherDetails?.dob, 'DD/MM/YYYY') : '---'}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">Gender</Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {formatText(teacherDetails?.gender ? teacherDetails?.gender : '---')}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">
                      Registration On
                    </Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {teacherDetails?.registration_date
                        ? formatDate(teacherDetails?.registration_date, 'DD/MM/YYYY')
                        : '---'}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">Document</Typography>
                    <Box className="flex items-center gap-2">
                      {/* Document Logo */}
                      <img src={DocumentLogo} alt="Document" className="w-[18] h-[18]" />

                      {/* Document Name */}
                      <Typography className="personal-information-value-text">
                        Document.png
                      </Typography>

                      {/* View Button */}
                      {(() => {
                        const fileUrl = teacherDetails.document;
                        const fileExtension = fileUrl?.split('.').pop()?.toLowerCase();
                        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(
                          fileExtension
                        );

                        if (isImage) {
                          return (
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download={false}
                              className="view-button"
                            >
                              View
                            </a>
                          );
                        } else {
                          return (
                            <a
                              href={`https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(fileUrl)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="view-button"
                            >
                              View
                            </a>
                          );
                        }
                      })()}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box className="flex flex-col gap-1 w-[100%]">
                <Typography className="personal-information-text">Bank Information</Typography>
                <Box className="flex flex-wrap items-center content-center gap-6 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%] bank-info-container">
                  <Box className="flex flex-col gap-2 w-[30%] bank-info-field">
                    <Typography className="personal-information-key-text">Full Name</Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {formatText(bankDetails?.full_name ? bankDetails?.full_name : '---')}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%] bank-info-field">
                    <Typography className="personal-information-key-text">Address</Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {formatText(bankDetails?.address ? bankDetails?.address : '---')}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%] bank-info-field">
                    <Typography className="personal-information-key-text">IBAN Number</Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {bankDetails?.IBAN_no ? bankDetails?.IBAN_no : '---'}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%] bank-info-field">
                    <Typography className="personal-information-key-text">
                      Bank Statement
                    </Typography>
                    <Box className="flex items-center gap-2 bank-document-section">
                      {/* Document Logo */}
                      <img src={DocumentLogo} alt="Document" className="w-[18] h-[18]" />

                      {/* Document Name */}
                      <Typography className="personal-information-value-text">
                        Bank Statement.png
                      </Typography>

                      {/* View Button */}
                      {(() => {
                        const fileUrl = bankDetails.statement_url;
                        const fileExtension = fileUrl?.split('.').pop()?.toLowerCase();
                        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(
                          fileExtension
                        );

                        if (isImage) {
                          return (
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download={false}
                              className="view-button"
                            >
                              View
                            </a>
                          );
                        } else {
                          return (
                            <a
                              href={`https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(fileUrl)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="view-button"
                            >
                              View
                            </a>
                          );
                        }
                      })()}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box className="flex flex-col gap-1 w-[100%]">
                <Typography className="personal-information-text">Education Background</Typography>
                <Box className="flex flex-wrap items-center content-center gap-6 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">Degree Name</Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {formatText(
                        teacherDetails?.degree_name ? teacherDetails?.degree_name : '---'
                      )}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">Institution</Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {formatText(teacherDetails?.institute ? teacherDetails?.institute : '---')}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">
                      Highest Qualification
                    </Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {formatText(
                        teacherDetails?.highest_qualification
                          ? teacherDetails?.highest_qualification
                          : '---'
                      )}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">Passing Year</Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {teacherDetails?.passing_year ? teacherDetails?.passing_year : '---'}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">CV</Typography>
                    <Box className="flex items-center gap-2">
                      {/* Document Logo */}
                      <img src={DocumentLogo} alt="Document" className="w-[18] h-[18]" />

                      {/* Document Name */}
                      <Typography className="personal-information-value-text">CV.pdf</Typography>

                      {/* View Button */}
                      {(() => {
                        const fileUrl = teacherDetails.cv;
                        const fileExtension = fileUrl?.split('.').pop()?.toLowerCase();
                        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(
                          fileExtension
                        );

                        if (isImage) {
                          return (
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download={false}
                              className="view-button"
                            >
                              View
                            </a>
                          );
                        } else {
                          return (
                            <a
                              href={`https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(fileUrl)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="view-button"
                            >
                              View
                            </a>
                          );
                        }
                      })()}
                    </Box>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">
                      Academic Transcript
                    </Typography>
                    <Box className="flex items-center gap-2">
                      {/* Document Logo */}
                      <img src={DocumentLogo} alt="Document" className="w-[18] h-[18]" />

                      {/* Document Name */}
                      <Typography className="personal-information-value-text">trans.png</Typography>

                      {/* View Button */}
                      {(() => {
                        const fileUrl = teacherDetails.degree;
                        const fileExtension = fileUrl?.split('.').pop()?.toLowerCase();
                        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(
                          fileExtension
                        );

                        if (isImage) {
                          return (
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download={false}
                              className="view-button"
                            >
                              View
                            </a>
                          );
                        } else {
                          return (
                            <a
                              href={`https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(fileUrl)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="view-button"
                            >
                              View
                            </a>
                          );
                        }
                      })()}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box className="flex flex-col gap-1 w-[100%]">
                <Typography className="personal-information-text">
                  Area of Specialisation
                </Typography>
                <Box className="flex flex-wrap items-center content-center gap-6 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">
                      Levels to Teach
                    </Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {teacherDetails?.level_of_teaching &&
                      teacherDetails.level_of_teaching.length > 0
                        ? teacherDetails.level_of_teaching
                            .map(level => LevelToTeachColor[level]?.value2)
                            .join(', ')
                        : '---'}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">
                      Years of Experience
                    </Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {teacherDetails?.years_of_experience
                        ? teacherDetails?.years_of_experience
                        : '---'}
                    </Typography>
                  </Box>
                  <Box className="flex flex-col gap-2 w-[30%]">
                    <Typography className="personal-information-key-text">
                      Instructing Languages
                    </Typography>
                    <Typography className="personal-information-value-text bank-info-value">
                      {formatText(
                        teacherDetails?.teaching_lang && teacherDetails.teaching_lang.length > 0
                          ? teacherDetails.teaching_lang.join(', ')
                          : '---'
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box className="flex flex-col gap-1 w-[100%]">
                <Typography className="personal-information-text">Subjects</Typography>
                <Box className="flex flex-wrap items-center content-center gap-6 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
                  {teacherDetails?.subjects && teacherDetails.subjects.length > 0 ? (
                    <Box className="flex flex-col w-full">
                      {teacherDetails.subjects.map(subject => (
                        <Box key={subject.id} className="subject-category-container">
                          <Typography className="subject-category-title">
                            {subject.title}
                          </Typography>
                          {subject.tags && subject.tags.length > 0 && (
                            <Box className="subject-tags-container">
                              {subject.tags.map(tag => (
                                <Box key={tag.id} className="subject-tag">
                                  {tag.title}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography className="personal-information-value-text">---</Typography>
                  )}
                </Box>
              </Box>
              {(teacherDetails?.approval_status === 'approval_pending' ||
                commentList.length > 0) && (
                <Box className="flex flex-col gap-1 w-[100%]">
                  <Typography className="personal-information-text">Comments</Typography>
                  {commentList.length > 0 && (
                    <Box className="flex flex-wrap items-center content-center gap-2 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
                      {commentList.length > 0 &&
                        commentList?.map((item, index) => {
                          return (
                            <Box
                              key={index}
                              className="flex flex-wrap items-center content-center gap-6 self-stretch p-3 rounded-md bg-[#FFF] w-[100%]"
                            >
                              <Box className="flex items-start gap-4 w-full">
                                <Avatar
                                  src={
                                    item?.profile_image && item.profile_image.startsWith('http')
                                      ? item.profile_image
                                      : undefined
                                  }
                                  alt="admin"
                                  sx={{
                                    width: '50px',
                                    height: '50px',
                                    bgcolor:
                                      item?.profile_image && item.profile_image.startsWith('http')
                                        ? 'transparent'
                                        : '#949DA6',
                                    color: '#FFFFFF',
                                    fontSize: '18px',
                                    fontWeight: 600,
                                  }}
                                  onError={e => {
                                    e.target.style.display = 'none';
                                  }}
                                >
                                  {(!item?.profile_image ||
                                    !item.profile_image.startsWith('http')) &&
                                    (item?.first_name || item?.last_name) &&
                                    getInitials(item.first_name, item.last_name)}
                                </Avatar>
                                <Box className="flex flex-col gap-2 w-full">
                                  <Box className="flex items-center gap-10">
                                    <Box className="flex items-center gap-2">
                                      <div className="personal-information-value-text">
                                        {item?.first_name} {item?.last_name}
                                      </div>
                                      {getCommentRole(item) && (
                                        <Box
                                          sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '4px',
                                            backgroundColor: '#F3F4F6',
                                            color: '#6B7280',
                                            padding: '2px 8px',
                                            fontFamily: 'Poppins',
                                            fontSize: '12px',
                                            fontStyle: 'normal',
                                            fontWeight: 400,
                                            lineHeight: '16px',
                                          }}
                                        >
                                          {getCommentRole(item)}
                                        </Box>
                                      )}
                                    </Box>
                                    <div className="day-time-text">
                                      {formatDateWithTime(item?.created_at).replace(
                                        /AM|PM/i,
                                        match => match.toLowerCase()
                                      )}
                                    </div>
                                  </Box>
                                  <div className="personal-information-value-text">
                                    {item?.comment}
                                  </div>
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                    </Box>
                  )}
                  {teacherDetails?.approval_status === 'approval_pending' && (
                    <Box className="flex flex-wrap items-center content-center gap-6 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
                      <Box className="flex items-start gap-4 w-full">
                        <Avatar
                          src={
                            auth?.profile_image && auth.profile_image.startsWith('http')
                              ? auth.profile_image
                              : undefined
                          }
                          alt="admin"
                          sx={{
                            width: '50px',
                            height: '50px',
                            bgcolor:
                              auth?.profile_image && auth.profile_image.startsWith('http')
                                ? 'transparent'
                                : '#949DA6',
                            color: '#FFFFFF',
                            fontSize: '18px',
                            fontWeight: 600,
                          }}
                          onError={e => {
                            e.target.style.display = 'none';
                          }}
                        >
                          {(!auth?.profile_image || !auth.profile_image.startsWith('http')) &&
                            (auth?.first_name || auth?.last_name) &&
                            getInitials(auth.first_name, auth.last_name)}
                        </Avatar>
                        <Box className="flex flex-col gap-2 w-full">
                          <Box className="flex items-center gap-2">
                            <div className="personal-information-value-text">
                              {auth?.first_name} {auth?.last_name}
                            </div>
                            {rbacMe && Array.isArray(rbacMe.roles) && rbacMe.roles.length > 0 && (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '4px',
                                  backgroundColor: '#F3F4F6',
                                  color: '#6B7280',
                                  padding: '2px 8px',
                                  fontFamily: 'Poppins',
                                  fontSize: '12px',
                                  fontStyle: 'normal',
                                  fontWeight: 400,
                                  lineHeight: '16px',
                                }}
                              >
                                {formatRole(rbacMe.roles)}
                              </Box>
                            )}
                          </Box>
                          <Input
                            type="text"
                            placeholder="Write your comment here"
                            value={comment}
                            onChange={handleCommentChange}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSubmit();
                              }
                            }}
                            sx={{
                              textDecoration: 'none',
                              border: '1px solid var(--Stroke, #D5DEE3)',
                              borderRadius: '6px',
                              '&:before, &:after': {
                                borderBottom: 'none !important',
                              },
                              '&:hover:not(.Mui-disabled):before': {
                                borderBottom: 'none !important',
                              },
                              padding: '12px 16px',
                              '& .MuiInputBase-input': {
                                padding: '10px',
                                fontFamily: 'Poppins',
                                fontSize: '14px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: '20px',
                                color: 'var(--Text, #14171F)',
                                '&::placeholder': {
                                  fontFamily: 'Poppins',
                                  fontSize: '14px',
                                  fontStyle: 'normal',
                                  fontWeight: 400,
                                  lineHeight: '20px',
                                  color: 'var(--Text, #14171F)',
                                  opacity: 1,
                                },
                              },
                              width: '100%',
                              height: '45px',
                              background: '#FFF',
                            }}
                            disabled={isSubmitting}
                            endAdornment={
                              <button
                                className="submit-button-text"
                                onClick={handleSubmit}
                                disabled={isSubmitting || comment.trim().length === 0}
                              >
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                              </button>
                            }
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {openApproveModal && (
        <ApproveModal
          setOpenApproveModal={setOpenApproveModal}
          rowId={rowId}
          teacherName={teacherName}
        />
      )}
      {openRejectModal && (
        <RejectModal
          setOpenRejectModal={setOpenRejectModal}
          rowId={rowId}
          teacherName={teacherName}
        />
      )}
    </Layout>
  );
};

export default RequestDetails;
